import { createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error(
    "Missing Supabase configuration. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env",
  );
}

export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Bypass the navigator LockManager. By default supabase-js serialises
    // auth-token access through navigator.locks; when a lock is held by a
    // backgrounded/stuck tab (or several tabs/PWA windows are open), getSession()
    // blocks until the lock frees. Because every PostgREST query AND every
    // edge-function call fetches the token first, the whole app then stalls for
    // ~15s — the symptoms we saw: blank screens, "fetchProfile timed out", and
    // "Failed to send a request to the Edge Function" (emails never sent).
    // A pass-through lock runs the work immediately with no global lock. Trade-off:
    // in rare multi-tab cases two tabs could refresh the token concurrently; for
    // this app that's far preferable to the hang.
    lock: async (_name, _acquireTimeout, fn) => fn(),
  },
  global: {
    headers: { "x-application-name": "nestd-web" },
  },
});

// Storage bucket helpers — keep paths consistent across the app.
export const STORAGE_BUCKETS = {
  contracts: "contracts",
  receipts: "receipts",
  studentDocs: "student-docs",
  inspectionPhotos: "inspection-photos",
  maintenancePhotos: "maintenance-photos",
  nsfasStatements: "nsfas-statements",
  avatars: "avatars",
} as const;

/**
 * Build a public-style URL for a private bucket via signed URLs.
 * Returns null if the file doesn't exist or the user can't access it.
 */
export async function signedUrl(
  bucket: string,
  path: string,
  expiresInSeconds = 3600,
): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresInSeconds);
  if (error) return null;
  return data.signedUrl;
}
